/**
 * Agent Inbox hook — Commander's unified message feed.
 *
 * Reads from the comms-owned `v_chat_inbox` view via SL slug
 * `/v1/views/chat-inbox` (Gate 4 of MO-FLEET-IDENTITY-CONVERGENCE-V1,
 * doc `0d71c5c0`). The view scopes by `auth.uid()` server-side — no
 * `'commander'` literal on the FE.
 *
 * Writes via direct SL POST (commander → agent).
 * MO-COMMANDER-INBOX (ce847832).
 *
 * Decomposed (House Rule #2 ≤300 LOC):
 *   - `useAgentInbox.types` — AgentSignal / SendMessagePayload / KNOWN_AGENTS / inboxKeys
 *   - `useAgentInbox.fetchers` — fetchInbox / fetchInboxPage / sortInboxChronological
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { post } from '@appic/lib/http/serviceLayerClient'
import { DATA_CLASS } from './dataClasses'
import {
  type AgentSignal,
  type SendMessagePayload,
  inboxKeys,
} from './useAgentInbox.types'
import {
  fetchInbox,
  fetchInboxPage,
  sortInboxChronological,
} from './useAgentInbox.fetchers'
import { inboxRefetchInterval, seedFromKey } from './useAgentInbox.poll'

export {
  type AgentSignal,
  type SendMessagePayload,
  type SendMessageAttachment,
  type AgentName,
  type BadgeVariant,
  KNOWN_AGENTS,
  AGENT_BADGE,
  inboxKeys,
} from './useAgentInbox.types'

export function useAgentInbox(
  agentOrOptions?: string | { enabled?: boolean },
  sessionFilterOrOptions?: string | null | { enabled?: boolean },
  options?: { enabled?: boolean },
) {
  const agent = typeof agentOrOptions === 'string' ? agentOrOptions : undefined
  const sessionFilter = typeof sessionFilterOrOptions === 'string' || sessionFilterOrOptions === null
    ? sessionFilterOrOptions
    : null
  const resolvedOptions = (typeof agentOrOptions === 'object' && agentOrOptions !== null
    ? agentOrOptions
    : typeof sessionFilterOrOptions === 'object' && sessionFilterOrOptions !== null
      ? sessionFilterOrOptions
      : options) as { enabled?: boolean } | undefined
  return useQuery({
    queryKey: inboxKeys.feed(agent, sessionFilter),
    // Thread React Query's signal so a refetch/unmount aborts AT the request
    // (→ handled QueryCancelledError), not a raw unhandled AbortError
    // (BUG-2026-06-15-FTJP6/W355J).
    queryFn: ({ signal }) => fetchInbox(agent, sessionFilter, undefined, signal),
    staleTime: DATA_CLASS.LIVE,
    // Circuit-breaker: ~3s live cadence, escalating to a 60s trickle (driven by
    // time-since-last-success, not the per-fetch fetchFailureCount) while the
    // view/SL is degraded so the poller stops amplifying a 429 storm. Jitter is
    // seeded from the stable queryHash (not Math.random) so unrelated re-renders
    // don't re-randomize the interval and thrash the poll timer.
    refetchInterval: (query) =>
      inboxRefetchInterval(query.state, Date.now(), () => seedFromKey(query.queryHash)),
    enabled: resolvedOptions?.enabled ?? true,
  })
}

/**
 * Paginated variant of useAgentInbox — load older messages on scrollback.
 * §F1 enabler. The first page is the latest 50; `fetchPreviousPage` fetches
 * the next 50 strictly older than the current oldest. AgentInbox binds
 * fetchPreviousPage to the virtualizer's near-top scroll.
 *
 * The first page also refetches every 3s so the chat tail stays live;
 * `getNextPageParam` returns undefined because newer messages arrive
 * via that interval, not via pagination.
 */
export function useAgentInboxPaginated(
  agent?: string,
  sessionFilter?: string | null,
  lane?: string | null,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: [...inboxKeys.feed(agent, sessionFilter, lane), 'paginated'] as const,
    // Thread the signal (see useAgentInbox) — the 3s refetchInterval × parallel
    // bridge queries was the unhandled-AbortError flood source.
    queryFn: ({ pageParam, signal }) => fetchInboxPage(agent, sessionFilter, pageParam, lane, signal),
    initialPageParam: undefined as string | undefined,
    getPreviousPageParam: (firstPage) => {
      if (!firstPage || firstPage.length === 0) return undefined
      // Pages are sorted chronologically (oldest first). The cursor
      // for the next-older page is the oldest message we have.
      return firstPage[0]?.created_at
    },
    getNextPageParam: () => undefined,
    staleTime: DATA_CLASS.LIVE,
    // Circuit-breaker: see useAgentInbox — heals to ~3s on success, escalates
    // to a 60s trickle (time-since-success driven) while failing instead of
    // hammering every 3s. queryHash-seeded jitter (stable across re-renders).
    refetchInterval: (query) =>
      inboxRefetchInterval(query.state, Date.now(), () => seedFromKey(query.queryHash)),
    enabled: options?.enabled ?? true,
  })
}

/**
 * Unread count per agent — unconsumed signals TO commander, grouped by source.
 * Subscribes to the unfiltered inbox cache without creating a second fetcher.
 * The "All agents" AgentInbox already polls this key every 3s.
 */
export function useUnreadCounts(): Map<string, number> {
  const { data: messages } = useQuery({
    queryKey: inboxKeys.feed(),
    queryFn: () => fetchInbox(),
    staleTime: DATA_CLASS.LIVE,
    refetchInterval: false, // Don't poll — piggyback on AgentInbox's poller
  })

  if (!messages) return new Map()

  const counts = new Map<string, number>()
  for (const s of messages) {
    if (s.target === 'commander' && !s.consumed && s.source) {
      counts.set(s.source, (counts.get(s.source) ?? 0) + 1)
    }
  }
  return counts
}

export function useSendToAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ target, text, target_embodiment_id, attachments }: SendMessagePayload) => {
      const body: Record<string, unknown> = { target, text }
      if (target_embodiment_id) body.target_embodiment_id = target_embodiment_id
      if (attachments && attachments.length > 0) body.attachments = attachments
      const res = await post<AgentSignal>('/v1/commander/inbox/send', body)
      return res.data
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: inboxKeys.all })
      const feedKey = inboxKeys.feed(
        vars.useGroupInboxCache
          ? undefined
          : vars.inboxRoom ?? (vars.target === 'all' ? undefined : vars.target),
      )
      const previous = qc.getQueryData<AgentSignal[]>(feedKey) ?? []
      const optimisticId = `optimistic:${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      const optimisticPayload: Record<string, unknown> = { text: vars.text }
      if (vars.attachments && vars.attachments.length > 0) {
        optimisticPayload.attachments = vars.attachments
      }
      const optimistic: AgentSignal = {
        id: optimisticId,
        agent_name: null,
        signal_type: 'commander_outbound',
        source: 'commander',
        target: vars.target,
        payload: optimisticPayload as AgentSignal['payload'],
        consumed: false,
        consumed_at: null,
        created_at: new Date().toISOString(),
        context: null,
      }
      qc.setQueryData(feedKey, sortInboxChronological([...previous, optimistic]))
      return { previous, feedKey, optimisticId }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.feedKey) {
        qc.setQueryData(ctx.feedKey, ctx.previous)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: inboxKeys.all })
    },
  })
}
